import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Check, Minus, X } from "lucide-react";
import * as api from "../../lib/api";

// Short readable form of a cited URL: host + path, no scheme or query string.
export function shortUrl(url) {
  try {
    const u = new URL(url);
    const path = u.pathname === "/" ? "" : u.pathname.replace(/\/$/, "");
    return `${u.hostname.replace(/^www\./, "")}${path}` || url;
  } catch {
    return url;
  }
}

export function PageLink({ url, className = "" }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" title={url}
      className={`block truncate text-zinc-500 hover:text-zinc-900 hover:underline underline-offset-4 ${className}`}>
      {shortUrl(url)}
    </a>
  );
}

const PRIORITY_STYLE = {
  urgent: "bg-red-50 text-red-700 border-red-100",
  high: "bg-amber-50 text-amber-800 border-amber-100",
  normal: "bg-zinc-50 text-zinc-500 border-zinc-100",
};

export function PriorityBadge({ priority }) {
  return (
    <span className={`inline-block shrink-0 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider rounded border ${PRIORITY_STYLE[priority] || PRIORITY_STYLE.normal}`}>
      {priority}
    </span>
  );
}

// Google NL entity types -> highlight tint for things our page already names.
const TYPE_STYLE = {
  PERSON: "bg-violet-50 text-violet-900",
  ORGANIZATION: "bg-sky-50 text-sky-900",
  LOCATION: "bg-teal-50 text-teal-900",
  CONSUMER_GOOD: "bg-indigo-50 text-indigo-900",
  EVENT: "bg-fuchsia-50 text-fuchsia-900",
  WORK_OF_ART: "bg-pink-50 text-pink-900",
};
const TYPE_LABEL = {
  PERSON: "person", ORGANIZATION: "organization", LOCATION: "place", CONSUMER_GOOD: "product",
  EVENT: "event", WORK_OF_ART: "work", OTHER: "concept",
};
const MISSING_STYLE = "bg-red-100 text-red-900 ring-1 ring-red-200";
const MARK_STYLE = { missing: MISSING_STYLE, covered: "bg-emerald-50 text-emerald-900", other: "bg-zinc-100 text-zinc-800" };
const MARK_TITLE = {
  missing: "Most cited pages name this, yours doesn't",
  covered: "Your page covers this",
  other: "Neither your page nor most cited pages name this",
};

// `marks` are non-overlapping {start, end, kind, type?} spans computed by the backend
// over this exact text, so highlights match what the diagnosis counted.
function HighlightedText({ text, marks }) {
  const parts = [];
  let at = 0;
  for (const m of marks || []) {
    if (m.start < at || m.end > text.length) continue;
    if (m.start > at) parts.push(text.slice(at, m.start));
    parts.push(
      <mark key={m.start} className={`rounded px-0.5 ${MARK_STYLE[m.kind] || MARK_STYLE.other}`}
        title={`${MARK_TITLE[m.kind] || ""}${m.type ? ` · ${TYPE_LABEL[m.type] || "concept"}` : ""}`}>
        {text.slice(m.start, m.end)}
      </mark>,
    );
    at = m.end;
  }
  parts.push(text.slice(at));
  return <>{parts}</>;
}

const pct = (v) => `${Math.round((v || 0) * 100)}%`;

// One row per signal compared across our page and the cited ones; `good` marks the value as a strength.
const SIGNALS = [
  { label: "Title / H1 matches the question", value: (s) => pct(s.title_match), good: (s) => s.title_match >= 0.8 },
  { label: "Answers in the opening lines", value: (s) => pct(s.answer_first), good: (s) => s.answer_first >= 0.6 },
  { label: "Covers what the AI answer says", value: (s) => pct(s.answer_coverage), good: (s) => s.answer_coverage >= 0.5 },
  { label: "Question-style headings", value: (s) => s.question_headings, good: (s) => s.question_headings > 0 },
  { label: "Lists or tables", value: (s) => s.lists_tables, good: (s) => s.lists_tables >= 2 },
  { label: "Specific facts", value: (s) => `${Math.round((s.facts / 20) * 100)}%`, good: (s) => s.facts >= 16 },
  { label: "Structured data", value: (s) => s.schema.join(", ") || "none", good: (s) => s.schema.length > 0 },
  { label: "Last updated", value: (s) => s.updated_at || "no date", good: (s) => s.fresh },
  { label: "Words", value: (s) => s.word_count.toLocaleString(), good: () => null },
];

function Cell({ sig, row, behind }) {
  if (!sig) return <td className="px-3 py-1.5 text-zinc-300">couldn't read</td>;
  const good = row.good(sig);
  return (
    <td className={`px-3 py-1.5 tabular-nums whitespace-nowrap ${behind ? "bg-red-50 text-red-800 font-bold"
      : good === false ? "text-zinc-400" : "text-zinc-900"}`}>
      <span className="inline-flex items-center gap-1">
        {good === true && <Check size={11} strokeWidth={2.5} className="text-emerald-600" />}
        {good === false && <Minus size={11} strokeWidth={2.5} className={behind ? "text-red-400" : "text-zinc-300"} />}
        {row.value(sig)}
      </span>
    </td>
  );
}

const ENTITY_NOTE = {
  language: (e) => `Google's entity analysis doesn't support this language${e.language ? ` (${e.language})` : ""}, so highlights are word matches.`,
  api_error: () => "Google entity analysis is unavailable right now, so highlights are word matches.",
};

function ColumnHead({ c }) {
  return (
    <th className={`px-3 py-1.5 font-bold max-w-[10rem] ${c.key === "ours" ? "text-zinc-900" : "text-zinc-500"}`}>
      <a href={c.url} target="_blank" rel="noreferrer" title={c.url} className="block truncate hover:underline underline-offset-4">
        {c.label}
      </a>
    </th>
  );
}

// Why the latest AI Overview cited the pages it did, and what to change on ours.
export default function CitationDiagnosis({ queryId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getAiCitationDiagnosis(queryId, refresh));
    } catch (err) {
      console.error("[AiCitations] diagnosis failed:", { status: err.response?.status, message: err.message });
      setError(`Couldn't read the pages${err.response?.status ? ` (HTTP ${err.response.status})` : ""}.`);
    } finally {
      setLoading(false);
    }
  }, [queryId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) return <p className="text-xs text-zinc-400">Reading the cited pages and yours…</p>;
  if (error) return <p className="text-xs text-red-500">{error}</p>;
  if (!data) return null;
  if (data.status === "not_checked") return <p className="text-xs text-zinc-500">Not checked yet. Press Check now first.</p>;
  if (data.status === "no_overview") {
    return (
      <p className="text-xs text-zinc-500">
        Google showed no AI Overview for this search on the last check, so there was nothing to be cited in.
        Questions and how-to searches get Overviews far more often than short keyword searches.
      </p>
    );
  }

  const { ours, cited, actions } = data;
  const entities = data.entities || {};
  const columns = [
    ...(ours?.url ? [{ key: "ours", label: "Your page", url: ours.url, signals: ours.signals }] : []),
    ...cited.map((c, i) => ({ key: c.url, index: i, label: `#${c.position} ${c.source}`, url: c.url, signals: c.signals })),
  ];
  const readable = cited.filter((c) => c.signals);
  // Our cell is red when we fall short where most readable cited pages are strong.
  const behind = (row) =>
    ours?.signals && row.good(ours.signals) === false &&
    readable.filter((c) => row.good(c.signals)).length * 2 > readable.length;
  const urgent = actions.filter((a) => a.priority === "urgent").length;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-bold text-zinc-900">
          {data.status === "cited"
            ? `You're cited at #${data.our_position}. What keeps you there:`
            : actions.length ? "What to change to get cited" : "Your page already matches the cited pages on these signals"}
          {urgent > 0 && <span className="ml-2 text-xs font-bold text-red-600">{urgent} urgent</span>}
        </p>
        <button type="button" onClick={() => load(true)} disabled={loading} title="Read the pages again"
          className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-900 disabled:opacity-50">
          <RefreshCw size={11} strokeWidth={2} className={loading ? "animate-spin" : ""} /> Re-read
        </button>
      </div>

      {data.status === "cited" && ours?.strengths?.length > 0 && (
        <p className="text-xs text-zinc-700">{ours.strengths.join(" · ")}</p>
      )}

      {actions.length > 0 && (
        <ol className="space-y-1.5">
          {actions.map((a, i) => (
            <li key={a.key} className={`flex gap-2 text-xs rounded-lg px-2 py-1.5 ${a.priority === "urgent" ? "bg-red-50/60" : ""}`}>
              <span className="w-4 shrink-0 text-right tabular-nums font-bold text-zinc-400">{i + 1}</span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="font-bold text-zinc-900">{a.title}</span>
                  <PriorityBadge priority={a.priority} />
                </span>
                <span className="block text-zinc-500">{a.detail}</span>
              </span>
            </li>
          ))}
        </ol>
      )}

      {data.answer && (
        <div>
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">What the AI answered</p>
            <p className="flex items-center gap-2 text-[10px] text-zinc-500">
              <span className={`rounded px-1 ${MISSING_STYLE}`}>missing on your page</span>
              <span className="rounded px-1 bg-emerald-50 text-emerald-900">you cover it</span>
              {data.entities?.available && <span className="rounded px-1 bg-zinc-100 text-zinc-800">other entity</span>}
            </p>
          </div>
          <p className="text-xs leading-relaxed text-zinc-600">
            <HighlightedText text={data.answer} marks={data.answer_marks} />
          </p>
          {ENTITY_NOTE[entities.reason] && (
            <p className="text-[10px] text-zinc-400 mt-1">{ENTITY_NOTE[entities.reason](entities)}</p>
          )}
        </div>
      )}

      {entities.available && entities.rows?.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">
            Entities Google finds in the AI answer
          </p>
          <div className="overflow-x-auto border border-zinc-100 rounded-lg">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left border-b border-zinc-100">
                  <th className="px-3 py-1.5" />
                  {columns.map((c) => <ColumnHead key={c.key} c={c} />)}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {entities.rows.map((r) => {
                  const missing = entities.missing.includes(r.name);
                  return (
                    <tr key={r.key} className={missing ? "bg-red-50/50" : ""}>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="inline-block w-10 h-1 rounded-full bg-zinc-100 overflow-hidden" title={`Salience in the answer ${pct(r.salience)}`}>
                            <span className="block h-full bg-zinc-900" style={{ width: `${Math.min(100, r.salience * 200)}%` }} />
                          </span>
                          {r.wikipedia_url
                            ? <a href={r.wikipedia_url} target="_blank" rel="noreferrer" className="font-bold text-zinc-900 hover:underline underline-offset-4">{r.name}</a>
                            : <span className="font-bold text-zinc-900">{r.name}</span>}
                          <span className="text-zinc-400">{TYPE_LABEL[r.type] || "concept"}</span>
                        </span>
                      </td>
                      {columns.map((c) => {
                        const v = c.key === "ours" ? r.ours : r.cited[c.index];
                        const unread = !c.signals;
                        return (
                          <td key={c.key} className={`px-3 py-1.5 ${c.key === "ours" && missing ? "text-red-700 font-bold" : ""}`}>
                            {unread ? <span className="text-zinc-300">–</span>
                              : v === null || v === undefined
                                ? <X size={12} strokeWidth={2.5} className={c.key === "ours" && missing ? "text-red-500" : "text-zinc-300"} />
                                : <Check size={12} strokeWidth={2.5} className="text-emerald-600" />}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {entities.ours_top?.length > 0 && (
            <p className="text-[11px] text-zinc-500 mt-2">
              Google reads your page as about:{" "}
              {entities.ours_top.map((e) => (
                <span key={e.name} className={`inline-block rounded px-1 mr-1 mb-1 ${TYPE_STYLE[e.type] || "bg-zinc-100 text-zinc-700"}`}>
                  {e.name}
                </span>
              ))}
            </p>
          )}
        </div>
      )}

      {columns.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Why these pages get cited</p>
          <div className="overflow-x-auto border border-zinc-100 rounded-lg">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left border-b border-zinc-100">
                  <th className="px-3 py-1.5" />
                  {columns.map((c) => <ColumnHead key={c.key} c={c} />)}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {SIGNALS.map((row) => (
                  <tr key={row.label}>
                    <td className="px-3 py-1.5 text-zinc-500 whitespace-nowrap">{row.label}</td>
                    {columns.map((c) => (
                      <Cell key={c.key} sig={c.signals} row={row} behind={c.key === "ours" && behind(row)} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {ours?.url && !ours.cited && ours.position && (
            <p className="text-[10px] text-zinc-400 mt-1">Your page ranks #{Math.round(ours.position)} in Search Console for this search.</p>
          )}
        </div>
      )}

      <p className="text-[10px] text-zinc-400">{data.note}</p>
    </div>
  );
}
